import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Star, 
  Users, 
  Shield, 
  Building2, 
  FileText, 
  Calendar, 
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Download,
  Loader2,
  Check,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { listMembershipPlans, getUnifiedPlanDetails, calculateMembershipFee } from '../services/api';
import { withLocalizedPlan } from '../utils/membershipPlanLocalization';
import toast from 'react-hot-toast';

const MembershipPage = () => {
  const [activeTab, setActiveTab] = useState('plans');
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [unifiedPlan, setUnifiedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPlans, setExpandedPlans] = useState({});
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [benefits, setBenefits] = useState([]);
  const [eligibility, setEligibility] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [longBenefits, setLongBenefits] = useState([]);
  // Pricing calculator state
  const [workerCount, setWorkerCount] = useState('');
  const [feeCalculation, setFeeCalculation] = useState(null);
  const [calculatingFee, setCalculatingFee] = useState(false);

  // Update content when language changes
  useEffect(() => {
    const updateContent = () => {
      const currentLanguage = getCurrentLanguage();
      setCurrentLang(currentLanguage);
      
      setBenefits([
        {
          icon: Shield,
          title: getText('membership.benefits.regulatory.title'),
          description: getText('membership.benefits.regulatory.desc')
        },
        {
          icon: Building2,
          title: getText('membership.benefits.infrastructure.title'),
          description: getText('membership.benefits.infrastructure.desc')
        },
        {
          icon: Users,
          title: getText('membership.benefits.networking.title'),
          description: getText('membership.benefits.networking.desc')
        },
        {
          icon: FileText,
          title: getText('membership.benefits.documentation.title'),
          description: getText('membership.benefits.documentation.desc')
        },
        {
          icon: Calendar,
          title: getText('membership.benefits.training.title'),
          description: getText('membership.benefits.training.desc')
        },
        {
          icon: Phone,
          title: getText('membership.benefits.support.title'),
          description: getText('membership.benefits.support.desc')
        }
      ]);

      try {
        const lb = getText('membership.benefits.long.items');
        setLongBenefits(Array.isArray(lb) ? lb : []);
      } catch {}

      setEligibility([
        getText('membership.eligibility.item1'),
        getText('membership.eligibility.item2'),
        getText('membership.eligibility.item3'),
        getText('membership.eligibility.item4'),
        getText('membership.eligibility.item5'),
        getText('membership.eligibility.item6')
      ]);

      setRequirements([
        getText('membership.requirements.item1'),
        getText('membership.requirements.item2'),
        getText('membership.requirements.item3'),
        getText('membership.requirements.item4'),
        getText('membership.requirements.item5'),
        getText('membership.requirements.item6'),
        getText('membership.requirements.item7'),
        getText('membership.requirements.item8')
      ]);
    };

    // Listen for custom language change events
    window.addEventListener('languageChanged', updateContent);
    
    // Listen for storage changes (for cross-tab synchronization)
    window.addEventListener('storage', updateContent);
    
    // Initial load
    updateContent();

    return () => {
      window.removeEventListener('languageChanged', updateContent);
      window.removeEventListener('storage', updateContent);
    };
  }, []);

  // Load unified membership plan from API
  useEffect(() => {
    const loadPlans = async () => {
      setLoading(true);
      try {
        // Try to get unified plan first
        try {
          const unified = await getUnifiedPlanDetails();
          const localizedUnified = withLocalizedPlan(unified, currentLang);
          setUnifiedPlan(localizedUnified);
          setMembershipPlans([localizedUnified]);
        } catch (unifiedError) {
          // Fallback to list all plans
          const data = await listMembershipPlans();
          const plans = Array.isArray(data) ? data.filter(p => p.isActive).map(p => withLocalizedPlan(p, currentLang)) : [];
          const unified = plans.find(p => p.type === 'unified');
          if (unified) {
            setUnifiedPlan(unified);
            setMembershipPlans([unified]);
          } else {
            setMembershipPlans(plans);
          }
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to load membership plans:', e);
        }
        toast.error(getText('membership.plans.loadingError') || 'Failed to load membership plans. Please try again later.');
        setMembershipPlans([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadPlans();
    const interval = setInterval(loadPlans, 30000);
    return () => clearInterval(interval);
  }, [currentLang]);

  // Handle fee calculation
  const handleCalculateFee = async () => {
    const workerCountNum = parseInt(workerCount, 10);
    if (!workerCountNum || workerCountNum < 1) {
      toast.error(getText('membership.calculator.error.workerCount'));
      return;
    }
    
    setCalculatingFee(true);
    try {
      const feeData = await calculateMembershipFee(workerCountNum);
      setFeeCalculation(feeData);
    } catch (error) {
      toast.error(getText('membership.calculator.error.calculation'));
      setFeeCalculation(null);
    } finally {
      setCalculatingFee(false);
    }
  };

  // Get tier color
  const getTierColor = (tier) => {
    const colors = {
      'Bronze': 'from-amber-500 to-amber-600',
      'Silver': 'from-gray-400 to-gray-500',
      'Gold': 'from-yellow-400 to-yellow-500',
      'Platinum': 'from-purple-500 to-purple-600'
    };
    return colors[tier] || 'from-gray-500 to-gray-600';
  };

  // Get tier badge color
  const getTierBadgeColor = (tier) => {
    const colors = {
      'Bronze': 'bg-amber-100 text-amber-800 border-amber-200',
      'Silver': 'bg-gray-100 text-gray-800 border-gray-200',
      'Gold': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Platinum': 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800';
  };

  const toggleExpand = (planId) => {
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {getText('membership.title') || 'Membership Plans'}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('membership.subtitle') || 'Join IFM and unlock exclusive benefits for your industry'}
            </p>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="container">
          <div className="flex space-x-8 overflow-x-auto">
            {[
              { id: 'plans', name: getText('membership.plans.title') || 'Plans' },
              { id: 'benefits', name: getText('membership.benefits.title') || 'Benefits' },
              { id: 'eligibility', name: getText('membership.eligibility.title') || 'Eligibility' },
              { id: 'registration', name: getText('membership.cta.register') || 'Register' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-semibold text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-16">
        <div className="container">
          {/* Plans Tab */}
          {activeTab === 'plans' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {getText('membership.plans.title') || 'Choose Your Membership Plan'}
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  {getText('membership.plans.subtitle') || 'Select the plan that best fits your industry needs'}
                </p>
              </div>
              
              {loading ? (
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
                    <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
                  </div>
                  <p className="text-gray-600 font-medium">{getText('membership.plans.loading')}</p>
                </div>
              ) : !unifiedPlan ? (
                <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-200">
                  <p className="text-gray-600 mb-4">{getText('membership.plan.notAvailable')}</p>
                  <p className="text-sm text-gray-500">{getText('membership.plan.contactAdmin')}</p>
                </div>
              ) : (
                <div className="max-w-4xl mx-auto space-y-8">
                  {/* Unified Plan Card */}
                  <div className="bg-white rounded-2xl shadow-lg border-2 border-primary-200 overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-8">
                      <div className="mb-2">
                        <span className="text-sm font-semibold opacity-90 uppercase tracking-wide">
                          {getText('membership.plan.unified')}
                        </span>
                      </div>
                      <h3 className="text-3xl font-bold mb-2">{unifiedPlan.name}</h3>
                      {unifiedPlan.alternativeName && (
                        <p className="text-sm opacity-90 italic">{unifiedPlan.alternativeName}</p>
                      )}
                      <p className="text-sm opacity-90 mt-2">{unifiedPlan.description}</p>
                    </div>

                    {/* Pricing Calculator */}
                    <div className="p-8 border-b border-gray-200 bg-gradient-to-br from-gray-50 to-white">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">{getText('membership.calculator.title')}</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {getText('membership.calculator.workerCount')}
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              min="1"
                              value={workerCount}
                              onChange={(e) => setWorkerCount(e.target.value)}
                              placeholder={getText('membership.calculator.workerCountPlaceholder')}
                              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-primary-500"
                            />
                            <button
                              onClick={handleCalculateFee}
                              disabled={calculatingFee || !workerCount}
                              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                              {calculatingFee ? <Loader2 className="h-5 w-5 animate-spin" /> : getText('membership.calculator.calculate')}
                            </button>
                          </div>
                        </div>
                        <div>
                          {feeCalculation && (
                            <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                              <div className="text-sm text-gray-600 mb-1">{getText('membership.calculator.fee')}</div>
                              <div className="text-3xl font-bold text-primary-700">
                                ₹{Number(feeCalculation.fee).toLocaleString('en-IN')}
                              </div>
                              <div className="text-xs text-gray-600 mt-2">
                                {getText('membership.calculator.category')} {feeCalculation.pricingCategory}
                              </div>
                              {feeCalculation.breakdown && (
                                <div className="text-xs text-gray-600 mt-3 pt-3 border-t border-primary-200 space-y-1.5">
                                  {feeCalculation.breakdown.additionalWorkers > 0 ? (
                                    <>
                                      <div className="flex justify-between">
                                        <span>{getText('membership.calculator.baseFee')} ({feeCalculation.breakdown.baseWorkers} {getText('membership.calculator.members')}):</span>
                                        <span className="font-semibold">₹{Number(feeCalculation.breakdown.baseFee).toLocaleString('en-IN')}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span>{getText('membership.calculator.additional')} ({feeCalculation.breakdown.additionalWorkers} {getText('membership.calculator.members')}):</span>
                                        <span className="font-semibold">₹{Number(feeCalculation.breakdown.additionalFeeAmount).toLocaleString('en-IN')}</span>
                                      </div>
                                      <div className="flex justify-between pt-1.5 border-t border-primary-300 font-bold text-primary-800">
                                        <span>{getText('membership.calculator.total')}</span>
                                        <span>₹{Number(feeCalculation.fee).toLocaleString('en-IN')}</span>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="flex justify-between">
                                      <span>{getText('membership.calculator.baseFee')} ({feeCalculation.breakdown.totalWorkers} {feeCalculation.breakdown.totalWorkers === 1 ? getText('membership.calculator.member') : getText('membership.calculator.members')}):</span>
                                      <span className="font-semibold">₹{Number(feeCalculation.breakdown.baseFee).toLocaleString('en-IN')}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-sm text-blue-900">
                          <strong>{getText('membership.calculator.pricingStructure')}</strong>
                          <ul className="mt-2 space-y-1 text-blue-800">
                            <li>• {getText('membership.calculator.smallIndustries')} (≤{unifiedPlan.smallIndustryThreshold || 10} {getText('membership.calculator.workers')}): ₹{Number(unifiedPlan.smallIndustryPrice || 5000).toLocaleString('en-IN')} {getText('membership.calculator.oneTime')}</li>
                            <li>• {getText('membership.calculator.mediumLargeIndustries')} (&gt;{unifiedPlan.smallIndustryThreshold || 10} {getText('membership.calculator.workers')}): ₹{Number(unifiedPlan.smallIndustryPrice || 5000).toLocaleString('en-IN')} {getText('membership.calculator.base')} + ₹{unifiedPlan.perWorkerPrice || 100} {getText('membership.calculator.perAdditionalWorker')}</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Plan Details */}
                    <div className="p-8">
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-3">{getText('membership.plan.duration')}</h4>
                          <p className="text-gray-700">
                            {unifiedPlan.duration || 12} {(unifiedPlan.durationUnit || 'Months').toLowerCase()}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-3">{getText('membership.plan.trainingProgram')}</h4>
                          {unifiedPlan.trainingProgram && unifiedPlan.trainingProgram.enabled ? (
                            <p className="text-gray-700">
                              {unifiedPlan.trainingProgram.duration}{getText('membership.plan.trainingProgram.weekProgram')}
                            </p>
                          ) : (
                            <p className="text-gray-500">{getText('membership.plan.trainingProgram.notAvailable')}</p>
                          )}
                        </div>
                      </div>

                      {/* Features */}
                      {unifiedPlan.features && unifiedPlan.features.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-gray-900 mb-3">{getText('membership.plan.keyBenefits')}</h4>
                          <ul className="space-y-2">
                            {unifiedPlan.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-gray-700">
                                <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Training Program Details */}
                      {unifiedPlan.trainingProgram && unifiedPlan.trainingProgram.enabled && (
                        <div className="mb-6 p-6 bg-gradient-to-br from-primary-50 via-blue-50 to-primary-100 border-2 border-primary-200 rounded-lg shadow-sm">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-primary-600" />
                            {getText('membership.plan.trainingProgram.title')}
                          </h4>
                          <p className="text-gray-700 mb-4">
                            {getText('membership.plan.trainingProgram.description')}
                          </p>
                          
                          {unifiedPlan.trainingProgram.trainingAreas && unifiedPlan.trainingProgram.trainingAreas.length > 0 && (
                            <div className="mb-4">
                              <h5 className="font-semibold text-gray-900 mb-2">{getText('membership.plan.trainingProgram.areas')}</h5>
                              <ul className="grid md:grid-cols-2 gap-2">
                                {unifiedPlan.trainingProgram.trainingAreas.map((area, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                                    <CheckCircle className="h-4 w-4 text-primary-600 flex-shrink-0 mt-0.5" />
                                    <span>{area}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {unifiedPlan.trainingProgram.outcomes && unifiedPlan.trainingProgram.outcomes.length > 0 && (
                            <div>
                              <h5 className="font-semibold text-gray-900 mb-2">{getText('membership.plan.trainingProgram.outcomes')}</h5>
                              <ul className="grid md:grid-cols-2 gap-2">
                                {unifiedPlan.trainingProgram.outcomes.map((outcome, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                                    <CheckCircle className="h-4 w-4 text-primary-600 flex-shrink-0 mt-0.5" />
                                    <span>{outcome}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* CTA Button */}
                      <div className="pt-6 border-t border-gray-200">
                        <Link
                          to="/member-registration"
                          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-xl transition-all duration-300 group shadow-md hover:shadow-lg font-semibold"
                        >
                          <span>{getText('membership.join.registerNow')}</span>
                          <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Benefits Tab */}
          {activeTab === 'benefits' && (
            <div className="space-y-12">
              {/* Header Section */}
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {getText('membership.benefits.title') || 'Member Benefits'}
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  {getText('membership.benefits.subtitle') || 'Powerful advantages tailored for industries across Maharashtra'}
                </p>
              </div>

              {/* Main Benefits Grid */}
              {benefits.length > 0 && (
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
                    {getText('membership.benefits.core.title')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                    {benefits.map((benefit, index) => {
                      const Icon = benefit.icon;
                      const colorClasses = [
                        'from-blue-500 to-blue-600 bg-blue-50 border-blue-200 text-blue-700',
                        'from-emerald-500 to-emerald-600 bg-emerald-50 border-emerald-200 text-emerald-700',
                        'from-purple-500 to-purple-600 bg-purple-50 border-purple-200 text-purple-700',
                        'from-amber-500 to-amber-600 bg-amber-50 border-amber-200 text-amber-700',
                        'from-indigo-500 to-indigo-600 bg-indigo-50 border-indigo-200 text-indigo-700',
                        'from-rose-500 to-rose-600 bg-rose-50 border-rose-200 text-rose-700'
                      ];
                      const colors = colorClasses[index % colorClasses.length].split(' ');
                      
                      return (
                        <div 
                          key={index}
                          className="group bg-white rounded-xl shadow-md border-2 border-gray-200 p-6 hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
                        >
                          <div className={`bg-gradient-to-br ${colors[0]} ${colors[1]} rounded-xl w-16 h-16 flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                            <Icon className="h-8 w-8 text-white" />
                          </div>
                          <h4 className={`text-xl font-bold mb-3 ${colors[4]}`}>
                            {benefit.title}
                          </h4>
                          <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                            {benefit.description}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Detailed Benefits Section */}
              {longBenefits.length > 0 && (
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 md:p-10 shadow-lg border border-gray-200">
                  <div className="mb-8">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                      <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg p-2">
                        <Star className="h-6 w-6 text-white" />
                      </div>
                      {getText('membership.benefits.long.title') || 'Detailed Member Benefits'}
                    </h3>
                    <p className="text-gray-600 text-base md:text-lg">
                      {getText('membership.benefits.long.subtitle')}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {longBenefits.map((item, idx) => {
                      const parts = String(item).split(/:\s*/);
                      const hasHeading = parts.length > 1;
                      const heading = hasHeading ? parts[0] : null;
                      const body = hasHeading ? parts.slice(1).join(': ') : item;
                      
                      return (
                        <div 
                          key={idx} 
                          className="bg-white rounded-lg p-5 border-l-4 border-primary-500 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-1 flex-shrink-0">
                              <CheckCircle className="h-5 w-5 text-primary-600" />
                            </div>
                            <div className="flex-1">
                              {hasHeading ? (
                                <>
                                  <h4 className="font-bold text-gray-900 mb-2 text-base md:text-lg">
                                    {heading}
                                  </h4>
                                  <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                                    {body}
                                  </p>
                                </>
                              ) : (
                                <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                                  {body}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Additional Information Section */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 md:p-10">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
                  {getText('membership.whyChoose.title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <Users className="h-8 w-8 text-primary-600" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">{getText('membership.whyChoose.network.title')}</h4>
                    <p className="text-sm text-gray-600">{getText('membership.whyChoose.network.desc')}</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-emerald-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <Shield className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">{getText('membership.whyChoose.regulatory.title')}</h4>
                    <p className="text-sm text-gray-600">{getText('membership.whyChoose.regulatory.desc')}</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <Building2 className="h-8 w-8 text-purple-600" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">{getText('membership.whyChoose.infrastructure.title')}</h4>
                    <p className="text-sm text-gray-600">{getText('membership.whyChoose.infrastructure.desc')}</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <FileText className="h-8 w-8 text-amber-600" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">{getText('membership.whyChoose.documentation.title')}</h4>
                    <p className="text-sm text-gray-600">{getText('membership.whyChoose.documentation.desc')}</p>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Eligibility Tab */}
          {activeTab === 'eligibility' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{getText('membership.eligibility.title')}</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  {getText('membership.eligibility.subtitle')}
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{getText('membership.eligibility.title')}</h3>
                  <ul className="space-y-3">
                    {eligibility.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{getText('membership.requirements.title')}</h3>
                  <ul className="space-y-3">
                    {requirements.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <FileText className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Registration Tab */}
          {activeTab === 'registration' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{getText('membership.cta.title')}</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  {getText('membership.cta.subtitle')}
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <div className="card text-center">
                  <div className="inline-flex p-4 rounded-full bg-green-100 text-green-600 mb-4">
                    <Users className="h-12 w-12" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{getText('membership.registration.new.title')}</h3>
                  <p className="text-gray-600 mb-6">{getText('membership.registration.new.desc')}</p>
                  <Link to="/member-registration" className="btn-primary inline-flex items-center">
                    {getText('membership.registration.new.cta')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
                
                <div className="card text-center">
                  <div className="inline-flex p-4 rounded-full bg-blue-100 text-blue-600 mb-4">
                    <Calendar className="h-12 w-12" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{getText('membership.renewal.title')}</h3>
                  <p className="text-gray-600 mb-6">{getText('membership.renewal.desc')}</p>
                  <Link to="/renewal" className="btn-primary inline-flex items-center">
                    {getText('membership.renewal.cta')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
              
              <div className="text-center mt-12">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{getText('membership.help.title')}</h3>
                <p className="text-gray-600 mb-6">{getText('membership.help.desc')}</p>
              </div>
            </div>
          )}
        </div>
      </section>

    </div>
  );
};

export default MembershipPage; 
